import React, { useCallback, useContext, useMemo } from 'react';
import { useLocalizedStrings } from 'app/localization/Context';
import { getDeviceColorsRGB } from 'app/model/Device';
import { HistoryStackContext } from 'app/home/general-components/HistoryStackContext';

import { ReactComponent as IconWhite0Myhome } from 'images/room/white/0-MyHome.svg';
import { ReactComponent as IconWhite1Bed } from 'images/room/white/1-Bed.svg';
import { ReactComponent as IconWhite10Balcony } from 'images/room/white/10-Balcony.svg';
import { ReactComponent as IconWhite11Lobby } from 'images/room/white/11-Lobby.svg';
import { ReactComponent as IconWhite12Pc } from 'images/room/white/12-PC.svg';
import { ReactComponent as IconWhite13Desk } from 'images/room/white/13-Desk.svg';
import { ReactComponent as IconWhite2Kitchen } from 'images/room/white/2-Kitchen.svg';
import { ReactComponent as IconWhite3Bath } from 'images/room/white/3-Bath.svg';
import { ReactComponent as IconWhite4Toilet } from 'images/room/white/4-Toilet.svg';
import { ReactComponent as IconWhite5Couch } from 'images/room/white/5-Couch.svg';
import { ReactComponent as IconWhite6Royal } from 'images/room/white/6-Royal.svg';
import { ReactComponent as IconWhite7Garden } from 'images/room/white/7-Garden.svg';
import { ReactComponent as IconWhite8Pool } from 'images/room/white/8-Pool.svg';
import { ReactComponent as IconWhite9Hallway } from 'images/room/white/9-Hallway.svg';

import { ReactComponent as IconYellow0Myhome } from 'images/room/yellow/0-MyHome.svg';
import { ReactComponent as IconYellow1Bed } from 'images/room/yellow/1-Bed.svg';
import { ReactComponent as IconYellow10Balcony } from 'images/room/yellow/10-Balcony.svg';
import { ReactComponent as IconYellow11Lobby } from 'images/room/yellow/11-Lobby.svg';
import { ReactComponent as IconYellow12Pc } from 'images/room/yellow/12-PC.svg';
import { ReactComponent as IconYellow13Desk } from 'images/room/yellow/13-Desk.svg';
import { ReactComponent as IconYellow2Kitchen } from 'images/room/yellow/2-Kitchen.svg';
import { ReactComponent as IconYellow3Bath } from 'images/room/yellow/3-Bath.svg';
import { ReactComponent as IconYellow4Toilet } from 'images/room/yellow/4-Toilet.svg';
import { ReactComponent as IconYellow5Couch } from 'images/room/yellow/5-Couch.svg';
import { ReactComponent as IconYellow6Royal } from 'images/room/yellow/6-Royal.svg';
import { ReactComponent as IconYellow7Garden } from 'images/room/yellow/7-Garden.svg';
import { ReactComponent as IconYellow8Pool } from 'images/room/yellow/8-Pool.svg';
import { ReactComponent as IconYellow9Hallway } from 'images/room/yellow/9-Hallway.svg';

import { ReactComponent as IconMore } from 'images/general/icon-more.svg';

import "./RoomHeaderTab.scss"

export default function RoomHeaderTab({
    roomsData,
    selectedRoomId, setSelectedRoomId }) {

    const s = useLocalizedStrings();
    const hs = useContext(HistoryStackContext);

    const roomList = useMemo(() => {
        return roomsData.roomsOrder.map((roomId) => roomsData.rooms[roomId]);
    }, [roomsData]);

    const onMoreClickH = useCallback((isActive, roomId) => {
        if (isActive) {
            return () => {
                hs.push("/settings/rooms_management/detail", { selectedRoomId: roomId })
            };
        } else {
            return undefined;
        }
    }, [hs]);

    if (roomsData.roomsOrder.length === 0) {
        return null;
    } else {
        return (
            <div className="room-header-tab">
                <div className="space" />
                <div
                    className={"my-home no-color-line room " + (selectedRoomId === undefined ? "active" : "inactive")}
                    onClick={() => { setSelectedRoomId(undefined) }}>
                    <RoomIcon avatar={0} className="icon" />
                    <div className="name">{s.myHome}</div>
                </div>
                {
                    roomList.map(room => {
                        const noColorLine = getDeviceColorsRGB(room.color) === "#000000";

                        return (
                            <div
                                key={"room-header-tab-room-id-" + String(room.room_id)}
                                className={
                                    "room" +
                                    (selectedRoomId === room.room_id ? " active" : " inactive") +
                                    (noColorLine ? " no-color-line" : "")
                                }
                                onClick={() => { setSelectedRoomId(room.room_id) }}>

                                <RoomIcon avatar={room.avatar} className="icon" />
                                {noColorLine ? null : (
                                    <div className="color-line" style={{ backgroundColor: getDeviceColorsRGB(room.color) }} />
                                )}
                                <div className="name">{room.name}</div>

                                <IconMore className="more" onClick={onMoreClickH(selectedRoomId === room.room_id, room.room_id)} />
                            </div>
                        );
                    })
                }
                <div className="space" />
            </div>
        )
    }
}

// color: "white" | "yellow"
export function getRoomIcon(avatar, color = "white") {
    if (color === "yellow") {
        switch (avatar) {
            case 1:
                return IconYellow1Bed
            case 2:
                return IconYellow2Kitchen
            case 3:
                return IconYellow3Bath
            case 4:
                return IconYellow4Toilet
            case 5:
                return IconYellow5Couch
            case 6:
                return IconYellow6Royal
            case 7:
                return IconYellow7Garden
            case 8:
                return IconYellow8Pool
            case 9:
                return IconYellow9Hallway
            case 10:
                return IconYellow10Balcony
            case 11:
                return IconYellow11Lobby
            case 12:
                return IconYellow12Pc
            case 13:
                return IconYellow13Desk
            case 0:
            default:
                return IconYellow0Myhome
        }
    } else {
        switch (avatar) {
            case 1:
                return IconWhite1Bed
            case 2:
                return IconWhite2Kitchen
            case 3:
                return IconWhite3Bath
            case 4:
                return IconWhite4Toilet
            case 5:
                return IconWhite5Couch
            case 6:
                return IconWhite6Royal
            case 7:
                return IconWhite7Garden
            case 8:
                return IconWhite8Pool
            case 9:
                return IconWhite9Hallway
            case 10:
                return IconWhite10Balcony
            case 11:
                return IconWhite11Lobby
            case 12:
                return IconWhite12Pc
            case 13:
                return IconWhite13Desk
            case 0:
            default:
                return IconWhite0Myhome
        }
    }
}

// color: "white" | "yellow"
export function RoomIcon({ avatar, className, color = "white" }) {
    const icon = getRoomIcon(avatar, color);
    return React.createElement(icon, { className: className });
}